// Diálogo para importar Entradas/Despesas de um S-26 para o S-44,
// com navegação por ano de serviço → trimestre → mês.
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FileSpreadsheet, Loader2, ArrowRightLeft, ChevronDown, CalendarDays, Search, Folder, FolderOpen } from 'lucide-react';
import { mapS26ToS44 } from '../s44Transfer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PT_MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

// Ano de serviço: setembro a agosto. Trimestres conforme fechamentos (nov/fev/mai/ago).
const TRIMESTERS = [
  { label: '1º Trimestre (set–nov)', months: [8, 9, 10] },
  { label: '2º Trimestre (dez–fev)', months: [11, 0, 1] },
  { label: '3º Trimestre (mar–mai)', months: [2, 3, 4] },
  { label: '4º Trimestre (jun–ago)', months: [5, 6, 7] },
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function classifyForm(form) {
  const fv = form.field_values || {};
  const text = `${fv['900_4_Text_C'] || ''} ${form.title || ''}`.toLowerCase();
  const mIdx = PT_MONTHS.findIndex((m) => text.includes(m));
  const title = form.title || '';
  const range = title.match(/(20\d{2})\s*[-–_/]\s*(20\d{2})/);
  let year = parseInt((fv['900_5_Text_C'] || '').trim(), 10);
  if (!year) {
    const m = title.match(/(20\d{2})/);
    year = m ? parseInt(m[1], 10) : NaN;
  }
  if (mIdx !== -1 && year) {
    let y = year;
    if (range && !parseInt((fv['900_5_Text_C'] || '').trim(), 10)) {
      y = mIdx >= 8 ? parseInt(range[1], 10) : parseInt(range[2], 10);
    }
    const startYear = mIdx >= 8 ? y : y - 1;
    const triIdx = TRIMESTERS.findIndex((t) => t.months.includes(mIdx));
    return { serviceYear: `${startYear}-${startYear + 1}`, triIdx, order: (mIdx + 4) % 12, monthLabel: `${cap(PT_MONTHS[mIdx])} ${y}` };
  }
  // Sem mês: tenta classificar pelo trimestre presente no título (ex.: "Ano 2025-2026 - 3º Trimestre")
  const triM = title.match(/([1-4])\s*º?\s*trimestre/i);
  if (triM && year) {
    const triIdx = parseInt(triM[1], 10) - 1;
    const serviceYear = range
      ? `${range[1]}-${range[2]}`
      : (triIdx === 0 ? `${year}-${year + 1}` : `${year - 1}-${year}`);
    return { serviceYear, triIdx, order: 50, monthLabel: null };
  }
  return null;
}

export default function ImportS26Dialog({ open, onOpenChange, onImport }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setSearch('');
    setLoading(true);
    axios.get(`${API}/audit-forms?category=manutencao&form_type=s26`)
      .then((r) => setForms(r.data.forms || []))
      .catch(() => toast.error('Erro ao listar formulários S-26'))
      .finally(() => setLoading(false));
  }, [open]);

  const tree = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? forms.filter((f) => {
          const info = classifyForm(f);
          return (f.title || '').toLowerCase().includes(q) || (info?.monthLabel || '').toLowerCase().includes(q);
        })
      : forms;
    const byYear = new Map();
    const outros = [];
    filtered.forEach((f) => {
      const info = classifyForm(f);
      if (!info) { outros.push(f); return; }
      if (!byYear.has(info.serviceYear)) byYear.set(info.serviceYear, new Map());
      const tris = byYear.get(info.serviceYear);
      if (!tris.has(info.triIdx)) tris.set(info.triIdx, []);
      tris.get(info.triIdx).push({ form: f, info });
    });
    // ordena anos desc, itens na ordem do ano de serviço
    const years = [...byYear.keys()].sort().reverse();
    years.forEach((y) => {
      byYear.get(y).forEach((list) => list.sort((a, b) => a.info.order - b.info.order));
    });
    return { years, byYear, outros };
  }, [forms, search]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleImport = async () => {
    if (!selectedId) { toast.error('Selecione uma folha S-26'); return; }
    setImporting(true);
    try {
      const { data } = await axios.get(`${API}/audit-forms/${selectedId}/field-values`);
      const result = mapS26ToS44(data.field_values || {});
      if (result.counts.entradas === 0 && result.counts.despesas === 0) {
        toast.warning('A folha S-26 selecionada não tem lançamentos para importar.');
        return;
      }
      onImport(result);
      onOpenChange(false);
      toast.success(`Importado: ${result.counts.entradas} entrada(s) e ${result.counts.despesas} despesa(s) do S-26.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar do S-26');
    } finally {
      setImporting(false);
    }
  };

  const formRow = (f, label) => (
    <button
      key={f.id}
      onClick={() => setSelectedId(f.id)}
      data-testid={`s26-option-${f.id}`}
      className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors flex items-center gap-2 ${
        selectedId === f.id ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:bg-accent'
      }`}
    >
      <FileSpreadsheet className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      <span className="truncate">{label || f.title}</span>
      {label && label !== f.title && (
        <span className="text-[11px] text-muted-foreground truncate">— {f.title}</span>
      )}
      <span className="ml-auto text-[11px] text-muted-foreground flex-shrink-0">
        {new Date(f.created_at).toLocaleDateString('pt-BR')}
      </span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="import-s26-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-primary" /> Importar do S-26
          </DialogTitle>
          <DialogDescription>
            Navegue pelos trimestres e escolha a folha do mês desejado. Lançamentos <strong>D</strong>/<strong>J</strong>{' '}
            viram Entradas e <strong>G</strong> viram Despesas, com totais calculados. <strong>A</strong>/<strong>R</strong>{' '}
            (aplicações e resgates) são internos e não são transferidos.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por mês ou título..."
            className="pl-8 text-sm"
            data-testid="import-s26-search"
          />
        </div>

        <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : tree.years.length === 0 && tree.outros.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma folha S-26 encontrada.</p>
          ) : (
            <>
              {tree.years.map((year) => (
                <div key={year} className="space-y-1.5">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5 pt-1">
                    <CalendarDays className="w-3.5 h-3.5" /> Ano de serviço {year}
                  </p>
                  {TRIMESTERS.map((tri, ti) => {
                    const items = tree.byYear.get(year).get(ti) || [];
                    const key = `${year}-${ti}`;
                    const isOpen = search.trim() ? items.length > 0 : !!expanded[key];
                    return (
                      <div key={key} className="border border-border rounded-lg overflow-hidden">
                        <button
                          onClick={() => items.length > 0 && toggle(key)}
                          data-testid={`tri-toggle-${key}`}
                          disabled={items.length === 0}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                            items.length === 0
                              ? 'bg-muted/20 text-muted-foreground/60 cursor-default'
                              : 'bg-muted/50 hover:bg-muted'
                          }`}
                        >
                          {isOpen
                            ? <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            : <Folder className={`w-4 h-4 flex-shrink-0 ${items.length === 0 ? 'text-muted-foreground/50' : 'text-amber-500'}`} />}
                          <span>{tri.label}</span>
                          <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
                            {items.length} {items.length === 1 ? 'folha' : 'folhas'}
                            {items.length > 0 && (
                              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                            )}
                          </span>
                        </button>
                        {isOpen && items.length > 0 && (
                          <div className="p-1.5 space-y-1">
                            {items.map(({ form, info }) => formRow(form, info.monthLabel))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              {tree.outros.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide pt-1">
                    Sem mês identificado
                  </p>
                  <div className="space-y-1">{tree.outros.map((f) => formRow(f))}</div>
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          As linhas de Entradas/Despesas atuais do S-44 serão substituídas. Revise e salve depois de importar.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="import-s26-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={handleImport} disabled={importing || !selectedId} data-testid="import-s26-confirm-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
